<script setup lang="ts">
import MarkdownIt from 'markdown-it'
import { VButton, VIcon } from '@/modules/shared/components'
import { useTaskListStore } from '../stores/tasking-store'
import { type Task } from '../types'
import TaskStatusCheckBox from './TaskStatusCheckBox.vue'

const props = defineProps<{
  isExpanded: boolean
  isLoading: boolean
  isUploading?: boolean
  task: Task
}>()

const emit = defineEmits<{
  (e: 'upload', task: Task): void
}>()

const store = useTaskListStore()

const toggleTaskStatus = (task: Task) => {
  if (store.isLoading(task.id)) return
  if (store.isTaskIncomplete(task)) {
    task.status = 'complete'
    task.children?.forEach((subtask) => {
      subtask.status = 'complete'
    })
    store.completeTask(task.id)
  } else {
    task.status = 'incomplete'
    task.children?.forEach((subtask) => {
      subtask.status = 'incomplete'
    })
    store.reopenTask(task.id)
  }
}

const markdown = new MarkdownIt()
</script>

<template>
  <div
    class="task cursor-pointer border-b border-gray-200 px-4 py-3.5 last:border-b-0"
    :class="store.isTaskComplete(task) ? 'opacity-50' : ''"
  >
    <div class="flex items-center justify-between gap-4">
      <div class="flex min-w-0 items-center gap-3">
        <!-- Chevron -->
        <div class="shrink-0 transition-transform" :class="isExpanded ? '' : '-rotate-90'">
          <VIcon name="ChevronDown" class="h-4 w-4 text-gray-400" />
        </div>
        <!-- Checkbox -->
        <div
          v-if="isLoading"
          class="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600"
        ></div>
        <TaskStatusCheckBox
          v-else
          @click.stop="() => toggleTaskStatus(task)"
          :checked="store.isTaskComplete(task)"
          :disabled="isLoading"
          class="shrink-0"
        />
        <!-- Content -->
        <div class="min-w-0">
          <span
            class="text-sm font-semibold"
            :class="store.isTaskComplete(task) ? 'text-gray-400 line-through' : 'text-gray-900'"
          >
            {{ task.name }}
          </span>
          <p v-if="!isExpanded" class="mt-0.5 truncate text-xs text-gray-400">
            {{ task.description }}
          </p>
        </div>
      </div>
      <!-- Action button -->
      <div class="flex shrink-0 items-center gap-2">
        <VButton
          @click.stop="emit('upload', task)"
          :disabled="store.isTaskComplete(task) || isUploading"
          :loading="isUploading"
          variant="secondary"
          size="sm"
        >
          <span class="inline-flex items-center gap-1.5">
            <svg
              v-if="!isUploading"
              xmlns="http://www.w3.org/2000/svg"
              class="h-3.5 w-3.5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path
                d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"
              />
            </svg>
            {{ isUploading ? 'Uploading…' : 'Upload' }}
          </span>
        </VButton>
      </div>
    </div>

    <!-- Expanded content -->
    <transition name="fade">
      <div v-if="isExpanded" class="ml-14 mt-2">
        <div
          @click.stop
          class="task-description text-xs leading-relaxed"
          :class="store.isTaskComplete(task) ? 'text-gray-400' : 'text-gray-500'"
          v-html="markdown.render(task.description || '')"
        ></div>
        <ul v-if="task.children?.length" class="mt-3 space-y-1.5">
          <li v-for="subtask in task.children" :key="subtask.id" @click.stop class="flex items-center gap-2">
            <TaskStatusCheckBox
              @click.stop="() => toggleTaskStatus(subtask)"
              :checked="store.isTaskComplete(subtask)"
              :disabled="store.isLoading(subtask.id)"
            />
            <span
              class="text-xs"
              :class="store.isTaskComplete(subtask) ? 'text-gray-400 line-through' : 'text-gray-700'"
            >
              {{ subtask.name }}
            </span>
          </li>
        </ul>
      </div>
    </transition>
  </div>
</template>

<style>
.task-description p {
  @apply m-0;
}

.task-description p a {
  @apply inline-block cursor-pointer whitespace-break-spaces font-medium text-[#51889B] underline decoration-[#51889B]/50 hover:text-[#3d778a] hover:decoration-[#3d778a]/60 active:scale-[0.99];
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
